/**
 * Компонент уведомления об обновлениях PWA
 * Показывает короткое полупрозрачное уведомление при обновлении приложения
 */
import { useEffect, useState, memo } from 'react'
import { useRegisterSW } from 'virtual:pwa-register/react'
import './UpdateNotification.css'

const APP_VERSION = import.meta.env.VITE_APP_VERSION || '0.2.6'
const LAST_VERSION_KEY = 'pwa_last_version'

export const UpdateNotification = memo(function UpdateNotification() {
  const [showUpdateSuccess, setShowUpdateSuccess] = useState(false)
  const [isUpdating, setIsUpdating] = useState(false)

  // Проверяем версию при загрузке приложения
  useEffect(() => {
    const lastVersion = localStorage.getItem(LAST_VERSION_KEY)
    console.log('[PWA] Version check:', { current: APP_VERSION, last: lastVersion })
    
    if (lastVersion && lastVersion !== APP_VERSION) {
      // Версия изменилась! Показываем уведомление
      console.log('[PWA] ✨ Version changed! Showing update notification:', lastVersion, '→', APP_VERSION)
      
      // Обновляем сохраненную версию
      localStorage.setItem(LAST_VERSION_KEY, APP_VERSION)
      
      // Показываем уведомление
      setShowUpdateSuccess(true)
      
      // Автоматически скрываем через 3 секунды
      setTimeout(() => {
        console.log('[PWA] Hiding update notification')
        setShowUpdateSuccess(false)
      }, 3000)
    } else if (!lastVersion) {
      // Первый запуск - сохраняем текущую версию
      console.log('[PWA] First launch, saving version:', APP_VERSION)
      localStorage.setItem(LAST_VERSION_KEY, APP_VERSION)
    } else {
      // Версия не изменилась
      console.log('[PWA] No version change, no notification')
    }
  }, [])

  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegistered(registration: ServiceWorkerRegistration | undefined) {
      console.log('[PWA] Service Worker registered, version:', APP_VERSION)
      
      // Проверяем обновления каждые 10 секунд для быстрой реакции
      if (registration) {
        setInterval(() => {
          console.log('[PWA] Checking for updates...')
          registration.update()
        }, 10000)
      }
    },
    onRegisterError(error: Error) {
      console.error('[PWA] Service Worker registration failed:', error)
    },
  })

  useEffect(() => {
    if (needRefresh && !isUpdating) {
      console.log('[PWA] 🎉 Обнаружено обновление! Версия:', APP_VERSION)
      handleUpdate()
    }
  }, [needRefresh, isUpdating])

  const handleUpdate = async () => {
    setIsUpdating(true)
    setNeedRefresh(false)
    
    try {
      console.log('[PWA] Применяем обновление...')
      
      // НЕ обновляем версию здесь - она обновится после перезагрузки при сравнении
      
      await updateServiceWorker(true)
      
      // Даем время Service Worker'у завершить все транзакции
      console.log('[PWA] Waiting for Service Worker to complete operations...')
      await new Promise(resolve => setTimeout(resolve, 1500))
      
      // Перезагружаем страницу
      console.log('[PWA] Reloading page...')
      window.location.reload()
    } catch (error) {
      console.error('[PWA] Failed to update:', error)
      setIsUpdating(false)
    }
  }

  // Показываем уведомление об успешном обновлении
  if (showUpdateSuccess) {
    console.log('[PWA] Rendering success notification for version:', APP_VERSION)
    return (
      <div className="update-notification update-notification--success">
        <div className="update-notification__content">
          <span className="update-notification__icon">✨</span>
          <div className="update-notification__text">
            <div className="update-notification__title">Приложение обновлено!</div>
            <div className="update-notification__message">Версия {APP_VERSION}</div>
          </div>
        </div>
      </div>
    )
  }

  // Показываем индикатор обновления (ПЕРЕД перезагрузкой)
  if (isUpdating) {
    console.log('[PWA] Rendering updating notification')
    return (
      <div className="update-notification">
        <div className="update-notification__content">
          <div className="update-notification__spinner"></div>
          <div className="update-notification__text">
            <div className="update-notification__title">Обновление...</div>
            <div className="update-notification__message">Подождите...</div>
          </div>
        </div>
      </div>
    )
  }

  return null
})


